/* The top of Billing: what is left of this period's credits, and how busy the
   workspace has been — runs per day over the last 30 days, from `/overview`. The
   credits come from billing; runs are what `/overview` counts, so the chart says
   runs and never pretends to be credits. */
import { useOverview } from "@/shared/api/overview";
import { RowList, GhostButton } from "@/shared/components/settings-atoms";
import { PLANS } from "@/shared/lib/plans";
import { formatCredits, shortDate } from "./format";

import type { BillingSummary } from "@/shared/types/billing";

const DAYS = 30;
const DAY_MS = 86_400_000;

/** Runs per day for the last `DAYS` days up to `today`, zero where nothing ran. */
function runsPerDay(daily: { date: string; runs: number }[], today: string) {
  const end = new Date(`${today}T00:00:00Z`).getTime();
  const runs = new Map<string, number>();
  for (const d of daily) runs.set(d.date, (runs.get(d.date) ?? 0) + d.runs);
  return Array.from({ length: DAYS }, (_, i) => {
    const date = new Date(end - (DAYS - 1 - i) * DAY_MS).toISOString().slice(0, 10);
    return { date, runs: runs.get(date) ?? 0 };
  });
}

export default function UsageCard({ summary, onUpgrade }: {
  summary: BillingSummary;
  /** Absent when there is no plan above this one, or the viewer may not buy. */
  onUpgrade?: () => void;
}) {
  const { data: overview } = useOverview(DAYS);
  const { plan, subscription, balance } = summary;
  const left = Math.max(0, Number(balance));
  const total = Number(plan.credits ?? 0);
  // A top-up can lift the balance past the plan's grant; the bar stops at full.
  const pct = total > 0 ? Math.min(100, Math.round((left / total) * 100)) : 100;
  const resets = subscription.current_period_end;
  const days = resets ? Math.max(0, Math.ceil((new Date(resets).getTime() - Date.now()) / DAY_MS)) : null;
  const name = PLANS.find((p) => p.id === plan.name)?.name ?? plan.name;
  const bars = overview ? runsPerDay(overview.daily, overview.today) : null;
  const peak = bars ? Math.max(1, ...bars.map((b) => b.runs)) : 1;
  const average = bars ? Math.round(bars.reduce((n, b) => n + b.runs, 0) / DAYS) : null;

  return (
    <RowList
      footer={
        <>
          <span className="mr-auto pl-1.5 text-[12.5px] text-[var(--text-tertiary)]">
            {resets ? <>Resets {shortDate(resets)}</> : "Top-ups never reset"}
          </span>
          {onUpgrade && <GhostButton onClick={onUpgrade}>Upgrade</GhostButton>}
        </>
      }
    >
      <div className="py-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] gap-x-10 gap-y-6">
        <div className="min-w-0">
          <div className="text-[14.5px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Credits this period</div>
          <div className="mt-0.5 text-[12.5px] text-[var(--text-tertiary)]">
            {name} plan{days != null && <> · resets in <span className="tabular-nums">{days}</span> {days === 1 ? "day" : "days"}</>}
          </div>
          <div className="mt-5 flex items-baseline gap-2 min-w-0">
            <span className="text-[32px] leading-none font-semibold tracking-[-0.02em] tabular-nums text-[var(--text-primary)]">
              {formatCredits(left)}
            </span>
            <span className="text-[13.5px] text-[var(--text-tertiary)] truncate">
              {total > 0 ? <>of <span className="tabular-nums">{formatCredits(total)}</span> credits left</> : "credits left"}
            </span>
            <span className="ml-auto shrink-0 text-[13.5px] tabular-nums text-[var(--text-secondary)]">{pct}%</span>
          </div>
          <span
            role="meter"
            aria-label="Credits left"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            className="mt-3 block h-2 w-full rounded-full bg-[var(--surface-2)] overflow-hidden"
          >
            <span
              className={`block h-full rounded-full transition-[width] duration-500 [transition-timing-function:var(--easing)] ${pct < 20 ? "bg-[var(--warn)]" : "bg-[var(--info)]"}`}
              style={{ width: `${pct}%` }}
            />
          </span>
        </div>

        <div className="min-w-0">
          <div className="flex items-baseline justify-between gap-3 text-[12.5px] text-[var(--text-tertiary)]">
            <span>Runs per day</span>
            {average != null && (
              <span>Avg <span className="tabular-nums text-[var(--text-primary)]">{average}</span> / day</span>
            )}
          </div>
          {/* The last bar is today's. Days with no runs keep a stub so the month stays readable. */}
          <div className="mt-3 h-[72px] flex items-end gap-[3px]" aria-hidden={!bars}>
            {(bars ?? Array.from({ length: DAYS }, () => ({ date: "", runs: 0 }))).map((b, i) => (
              <span
                key={b.date || i}
                title={b.date ? `${b.date}: ${b.runs} ${b.runs === 1 ? "run" : "runs"}` : undefined}
                className={`flex-1 min-w-0 rounded-[2px] transition-[height] duration-500 [transition-timing-function:var(--easing)] ${
                  i === DAYS - 1 && b.runs ? "bg-[var(--info)]" : b.runs ? "bg-[var(--disabled)]" : "bg-[var(--surface-2)]"
                }`}
                style={{ height: b.runs ? `${Math.max(8, Math.round((b.runs / peak) * 100))}%` : "3px" }}
              />
            ))}
          </div>
        </div>
      </div>
    </RowList>
  );
}
